/**
 * Artifact Inline
 *
 * Renders session artifacts by type:
 * - Images: thumbnail + click-to-expand
 * - PDFs: file icon + download button
 * - JSON: collapsible syntax-highlighted block
 * - Text: collapsible monospace block
 */

import { useState } from 'react'
import type { CSSProperties } from 'react'
import { ChevronDown, Download, FileText, Image as ImageIcon } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import type { SessionArtifact } from '../types'

export function ArtifactInline({ artifact }: { artifact: SessionArtifact }) {
  const [isExpanded, setIsExpanded] = useState(false)

  if (artifact.contentType && artifact.contentType.startsWith('image/')) {
    return (
      <div style={styles.imageContainer} data-testid={`artifact-image-${artifact.id}`}>
        {artifact.previewUrl && (
          <img
            src={artifact.previewUrl}
            alt={artifact.filename || 'Artifact'}
            style={styles.image}
          />
        )}
        {artifact.url && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => window.open(artifact.url, '_blank', 'noopener,noreferrer')}
            style={styles.fullWidthButton}
          >
            <ImageIcon style={styles.buttonIcon} />
            View Image
          </Button>
        )}
      </div>
    )
  }

  if (artifact.contentType === 'application/pdf') {
    return (
      <div style={styles.pdfContainer} data-testid={`artifact-pdf-${artifact.id}`}>
        <div style={styles.pdfHeader}>
          <FileText style={styles.fileIcon} />
          <span style={styles.filename}>
            {artifact.filename || 'Document.pdf'}
          </span>
          {artifact.size && (
            <span style={styles.fileSize}>{formatBytes(artifact.size)}</span>
          )}
        </div>
        {artifact.url && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => {
              const link = document.createElement('a')
              link.href = artifact.url!
              link.download = artifact.filename || 'document.pdf'
              link.click()
            }}
            style={styles.fullWidthButton}
          >
            <Download style={styles.buttonIcon} />
            Download
          </Button>
        )}
      </div>
    )
  }

  if (artifact.contentType === 'application/json') {
    return (
      <div style={styles.collapsibleContainer} data-testid={`artifact-json-${artifact.id}`}>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          style={styles.expandButton}
        >
          <ChevronDown
            style={{
              ...styles.chevron,
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
            className="transition-transform"
          />
          <span style={styles.monoLabel}>JSON</span>
        </Button>
        {isExpanded && (
          <pre style={styles.codeBlock}>
            {artifact.url}
          </pre>
        )}
      </div>
    )
  }

  // Default text renderer
  return (
    <div style={styles.collapsibleContainer} data-testid={`artifact-text-${artifact.id}`}>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => setIsExpanded(!isExpanded)}
        style={styles.expandButton}
      >
        <ChevronDown
          style={{
            ...styles.chevron,
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
          className="transition-transform"
        />
        <span style={styles.monoLabel}>{artifact.filename || 'Text'}</span>
      </Button>
      {isExpanded && (
        <pre style={{ ...styles.codeBlock, fontFamily: 'monospace' }}>
          {artifact.url}
        </pre>
      )}
    </div>
  )
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
}

const styles: Record<string, CSSProperties> = {
  imageContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  image: {
    maxHeight: '192px',
    maxWidth: '100%',
    borderRadius: 'var(--radius-sm)',
  },
  fullWidthButton: {
    width: '100%',
    fontSize: '12px',
  },
  buttonIcon: {
    width: '12px',
    height: '12px',
    marginRight: '8px',
  },
  pdfContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  pdfHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid rgba(128, 128, 128, 0.2)',
    background: 'rgba(0, 0, 0, 0.05)',
    padding: '8px',
  },
  fileIcon: {
    width: '16px',
    height: '16px',
  },
  filename: {
    flex: 1,
    fontSize: '12px',
    fontWeight: 500,
  },
  fileSize: {
    fontSize: '12px',
    opacity: 0.75,
  },
  collapsibleContainer: {
    borderRadius: 'var(--radius-sm)',
    border: '1px solid rgba(128, 128, 128, 0.2)',
    background: 'rgba(0, 0, 0, 0.05)',
  },
  expandButton: {
    width: '100%',
    justifyContent: 'flex-start',
    fontSize: '12px',
  },
  chevron: {
    width: '12px',
    height: '12px',
    marginRight: '8px',
  },
  monoLabel: {
    fontFamily: 'monospace',
    fontWeight: 600,
  },
  codeBlock: {
    overflowX: 'auto',
    borderTop: '1px solid rgba(128, 128, 128, 0.2)',
    padding: '8px',
    fontSize: '12px',
  },
}
